import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import GuardianAvailabilityStatus from './GuardianAvailabilityStatus';

const PHASE_ORDER = ["scan", "apply", "verify", "publish", "expire", "scheduler"];

const readable = (value) => String(value || "")
  .replaceAll("_", " ")
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const values = (items) => (items?.length ? items.join(", ") : "None declared");

export default function OperatorHealthCentre() {
  const [inventory, setInventory] = useState(null);
  const [overview, setOverview] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailVersion, setDetailVersion] = useState(0);
  const [selectedKey, setSelectedKey] = useState("");
  const [incidentFilter, setIncidentFilter] = useState("active");
  const [error, setError] = useState("");
  const [overviewError, setOverviewError] = useState("");
  const [runtimeError, setRuntimeError] = useState("");

  useEffect(() => {
    let active = true;
    api.get("/admin/operator-health/contracts")
      .then(({ data }) => {
        if (!active) return;
        setInventory(data);
        setSelectedKey(data.canonical_operator_keys?.[0] || "");
      })
      .catch((requestError) => {
        if (!active) return;
        setError(
          requestError.response?.data?.detail
          || requestError.message
          || "Unable to load the static contract inventory.",
        );
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    api.get("/admin/operator-health/overview")
      .then(({ data }) => {
        if (active) setOverview(data);
      })
      .catch((requestError) => {
        if (!active) return;
        setOverviewError(
          requestError.response?.data?.detail
          || requestError.message
          || "Advisory shadow history is unavailable.",
        );
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!selectedKey) return undefined;
    let active = true;
    setDetailLoading(true);
    setDetail(null);
    setRuntimeError("");
    api.get(`/admin/operator-health/operators/${encodeURIComponent(selectedKey)}?limit=20`)
      .then(({ data }) => {
        if (active) setDetail(data);
      })
      .catch((requestError) => {
        if (!active) return;
        setDetail(null);
        setRuntimeError(
          requestError.response?.data?.detail
          || requestError.message
          || "Operator health detail is unavailable.",
        );
      })
      .finally(() => {
        if (active) setDetailLoading(false);
      });
    return () => { active = false; };
  }, [selectedKey, detailVersion]);

  const profile = useMemo(
    () => inventory?.profiles?.find((item) => item.operator_key === selectedKey),
    [inventory, selectedKey],
  );
  const diagnostics = useMemo(
    () => (inventory?.diagnostics_inventory || [])
      .filter((item) => item.operator_key === selectedKey),
    [inventory, selectedKey],
  );
  const overviewState = useMemo(
    () => overview?.states?.find((item) => item.operator_key === selectedKey),
    [overview, selectedKey],
  );
  const runtimeState = detail?.state || overviewState;
  const visibleIncidents = useMemo(
    () => (detail?.incidents || []).filter((incident) => {
      if (incidentFilter === "all") return true;
      if (incidentFilter === "resolved") return incident.status === "resolved";
      return incident.status !== "resolved";
    }),
    [detail, incidentFilter],
  );

  const updateIncident = async (fingerprint, action) => {
    setRuntimeError("");
    const payload = {
      reason: `Admin ${action} from Operator Health Centre`,
    };
    if (action === "snooze") {
      payload.snoozed_until = new Date(
        Date.now() + (24 * 60 * 60 * 1000),
      ).toISOString();
    }
    try {
      await api.post(
        `/admin/operator-health/incidents/${encodeURIComponent(fingerprint)}/${action}`,
        payload,
      );
      setDetailVersion((value) => value + 1);
    } catch (requestError) {
      setRuntimeError(
        requestError.response?.data?.detail
        || requestError.message
        || "Unable to update incident notification state.",
      );
    }
  };

  if (error) {
    return (
      <><GuardianAvailabilityStatus />
      <div role="alert" className="border border-white/15 px-4 py-3 text-sm text-white/70">
        {error}
      </div>
      </>
    );
  }

  if (!inventory || !profile) {
    return <><GuardianAvailabilityStatus /><div className="py-8 text-sm text-white/50">Loading contract inventory...</div></>;
  }

  const sources = [
    ...(profile.discovery_sources || []).map((source) => ({ ...source, contractKind: "Discovery" })),
    ...(profile.detail_sources || []).map((source) => ({ ...source, contractKind: "Detail" })),
  ];

  return (
    <section data-testid="operator-health-centre" aria-labelledby="operator-health-heading">
      <GuardianAvailabilityStatus />
      <header className="mb-7">
        <div className="overline text-[var(--cc-gold)] mb-1">Health Centre</div>
        <h2 id="operator-health-heading" className="font-display text-2xl font-semibold">
          Operator health contracts
        </h2>
        <p
          className="mt-2 text-sm font-medium text-white/80"
          data-testid="operator-health-non-live-notice"
        >
          Contract and diagnostics inventory - not live operator health.
        </p>
        <p className="mt-1 max-w-3xl text-xs leading-5 text-white/50">
          Static contracts remain separate from runtime settings. Persisted runtime evidence,
          phase gates, recovery attempts, and incidents are shown in the control section below.
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-4 border-y border-white/10 py-4">
        <label className="block min-w-64 text-xs text-white/60" htmlFor="health-operator-select">
          Canonical operator
          <select
            id="health-operator-select"
            data-testid="health-operator-select"
            value={selectedKey}
            onChange={(event) => setSelectedKey(event.target.value)}
            className="mt-2 block w-full border border-white/15 bg-[var(--cc-navy)] px-3 py-2 text-sm text-white"
          >
            {inventory.profiles.map((item) => (
              <option
                key={item.operator_key}
                value={item.operator_key}
                label={`${item.display_name} (${item.operator_key})`}
              />
            ))}
          </select>
        </label>
        <div className="text-xs text-white/45">
          {inventory.profile_count} versioned profiles - {inventory.diagnostics_inventory.length} diagnostic entries
        </div>
      </div>

      <RuntimeHealthSummary
        state={runtimeState}
        detail={detail}
        loading={detailLoading}
        error={runtimeError || overviewError}
        incidentFilter={incidentFilter}
        setIncidentFilter={setIncidentFilter}
        visibleIncidents={visibleIncidents}
        updateIncident={updateIncident}
      />

      <section className="py-6" aria-labelledby="profile-metadata-heading">
        <h3 id="profile-metadata-heading" className="font-display text-lg font-semibold">Profile metadata</h3>
        <dl className="mt-4 grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
          <Metadata label="Canonical key" value={profile.operator_key} testId={`health-profile-${profile.operator_key}`} />
          <Metadata label="Display name" value={profile.display_name} />
          <Metadata label="Contract classification" value={readable(profile.mode_classification)} />
          <Metadata label="Profile version" value={profile.profile_version} />
          <Metadata label="Profile ID" value={profile.profile_id} mono />
          <Metadata label="Profile hash" value={profile.profile_hash} mono breakAll />
          <Metadata label="Source-of-truth module" value={profile.source_of_truth_module} mono breakAll />
          <Metadata label="Expected domains" value={values(profile.expected_domains)} />
          <Metadata label="Expected image domains" value={values(profile.expected_image_domains)} />
          <Metadata label="Last-known-good rules" value={values(profile.last_known_good_rules)} />
          <Metadata label="Quarantine rules" value={values(profile.quarantine_rules)} />
        </dl>
      </section>

      <ContractTable
        title="Source contracts"
        headings={["Kind", "Source ID", "Type / expectation", "Domains and paths", "Notes"]}
      >
        {sources.map((source) => (
          <tr key={`${source.contractKind}-${source.source_id}`} className="border-t border-white/10 align-top">
            <Cell>{source.contractKind}</Cell>
            <Cell mono>{source.source_id}</Cell>
            <Cell>{readable(source.source_type)}<br /><span className="text-white/45">{readable(source.availability)}</span></Cell>
            <Cell>{values(source.domains)}<br /><span className="text-white/45">{values(source.path_families)}</span></Cell>
            <Cell>{source.notes}</Cell>
          </tr>
        ))}
      </ContractTable>

      <ContractTable
        title="Identity rules"
        headings={["Priority", "Field", "Match method", "Source IDs", "Notes"]}
      >
        {[...(profile.identity_rules || [])]
          .sort((left, right) => left.priority - right.priority)
          .map((rule) => (
            <tr key={`${rule.priority}-${rule.field_name}`} className="border-t border-white/10 align-top">
              <Cell mono>{rule.priority}</Cell>
              <Cell mono>{rule.field_name}</Cell>
              <Cell>{readable(rule.match_method)}</Cell>
              <Cell mono>{values(rule.source_ids)}</Cell>
              <Cell>{rule.notes}</Cell>
            </tr>
          ))}
      </ContractTable>

      <ContractTable
        title="Trusted field evidence rules"
        headings={["Field", "Trusted sources", "Rules", "Currency / phases", "Freshness and notes"]}
      >
        {(profile.field_contracts || []).map((field) => (
          <tr key={field.field_name} className="border-t border-white/10 align-top">
            <Cell mono>{field.field_name}</Cell>
            <Cell mono>{values(field.trusted_source_ids)}</Cell>
            <Cell mono>{values(field.trusted_rules)}</Cell>
            <Cell>
              {values(field.supported_currencies)}
              <br />
              <span className="text-white/45">{values(field.required_for_phases)}</span>
            </Cell>
            <Cell>
              {field.freshness.configured ? `${field.freshness.max_age_hours} hours` : "Unconfigured"}
              <br />
              <span className="text-white/45">{field.notes}</span>
            </Cell>
          </tr>
        ))}
      </ContractTable>

      <section className="border-t border-white/10 py-6" aria-labelledby="capabilities-heading">
        <h3 id="capabilities-heading" className="font-display text-lg font-semibold">
          Declared contract capability
        </h3>
        <p className="mt-1 text-xs text-white/45">
          Capability describes reviewed implementation support. It is separate from runtime settings.
        </p>
        <div className="mt-4 grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
          {(profile.phase_contracts || []).map((phase) => (
            <div key={phase.phase} data-testid={`health-capability-${phase.phase}`}>
              <div className="text-xs font-semibold uppercase tracking-wide text-white/70">{phase.phase}</div>
              <div className="mt-1 text-sm">{phase.supported ? "Supported by contract" : "Not supported by contract"}</div>
              <div className="mt-1 text-xs leading-5 text-white/45">{phase.notes}</div>
              {phase.prerequisites?.length > 0 && (
                <div className="mt-1 text-xs text-white/45">Prerequisites: {values(phase.prerequisites)}</div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-white/10 py-6" aria-labelledby="recovery-heading">
        <h3 id="recovery-heading" className="font-display text-lg font-semibold">Reviewed recovery actions</h3>
        {profile.reviewed_recovery_actions?.length ? (
          <ul className="mt-4 divide-y divide-white/10 border-y border-white/10">
            {profile.reviewed_recovery_actions.map((action) => (
              <li key={action.action_id} className="py-3 text-sm">
                <span className="font-mono-num text-white/80">{action.action_id}</span>
                <span className="ml-3 text-white/45">{readable(action.tier)} - {values(action.phases)}</span>
                <div className="mt-1 text-xs text-white/50">{action.notes}</div>
              </li>
            ))}
          </ul>
        ) : <p className="mt-3 text-sm text-white/45">No reviewed recovery action is declared.</p>}
      </section>

      <ContractTable
        title="Diagnostics inventory"
        headings={["Phase", "Diagnostic key", "Value / semantic role", "Source module", "Description"]}
      >
        {[...diagnostics]
          .sort((left, right) => PHASE_ORDER.indexOf(left.phase) - PHASE_ORDER.indexOf(right.phase))
          .map((item) => (
            <tr key={`${item.phase}-${item.diagnostic_key}`} className="border-t border-white/10 align-top">
              <Cell>{readable(item.phase)}</Cell>
              <Cell mono>{item.diagnostic_key}</Cell>
              <Cell>{readable(item.value_kind)}<br /><span className="text-white/45">{readable(item.semantic_role)}</span></Cell>
              <Cell mono>{item.source_module}</Cell>
              <Cell>{item.description}</Cell>
            </tr>
          ))}
      </ContractTable>
    </section>
  );
}

function RuntimeHealthSummary({
  state,
  detail,
  loading,
  error,
  incidentFilter,
  setIncidentFilter,
  visibleIncidents,
  updateIncident,
}) {
  const currentState = state?.latest_runtime_state || state?.latest_shadow_state || "unknown";
  const history = detail?.recent_runs || [];
  const gates = state?.current_runtime_gates || [];
  const blockedPhases = gates.filter((gate) => gate.allowed === false);
  const settings = detail?.runtime_settings || {};
  const evidence = detail?.field_evidence || [];
  const anomalies = detail?.anomalies || [];
  const recoveries = detail?.recovery_attempts || [];
  const snapshots = detail?.snapshots_and_live_sets || [];
  const readModelWarnings = detail?.read_model_warnings || [];
  return (
    <section className="border-b border-white/10 py-6" aria-labelledby="runtime-health-heading">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h3 id="runtime-health-heading" className="font-display text-lg font-semibold">
            Deterministic health control
          </h3>
          <p className="mt-1 text-xs leading-5 text-white/50">
            Unsafe downstream phases are blocked against the exact evaluated evidence set.
          </p>
        </div>
        <span
          className="text-[11px] uppercase tracking-wide text-white/45"
          data-testid="health-runtime-gate-status"
        >
          {state?.runtime_gate_active ? "Runtime gates active" : "No evaluated runtime gate"}
        </span>
      </div>

      <dl className="mt-4 grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
        <Metadata label="Health state" value={readable(currentState)} testId="health-shadow-state" />
        <Metadata label="Last evaluated" value={formatDate(state?.last_evaluated_at)} />
        <Metadata label="Latest phase" value={state?.latest_phase ? readable(state.latest_phase) : "No history"} />
        <Metadata label="Recovery" value={readable(state?.recovery_status || "not_evaluated")} />
        <Metadata label="Open incidents" value={String(state?.open_incident_count || 0)} />
        <Metadata label="Scheduler switch" value={settings.scheduler_enabled ? "Enabled" : "Disabled"} />
        <Metadata label="Expiry switch" value={settings.expire_enabled ? "Enabled" : "Disabled"} />
      </dl>

      {state?.current_quality_reasons?.length > 0 && (
        <p className="mt-4 text-xs leading-5 text-white/55" data-testid="health-shadow-reasons">
          Reasons: {state.current_quality_reasons.map(readable).join(", ")}
        </p>
      )}
      {blockedPhases.length > 0 && (
        <div
          className="mt-4 border-y border-white/10 py-3 text-xs text-white/55"
          data-testid="health-runtime-blockers"
        >
          Blocked phases: {blockedPhases
            .map((item) => `${readable(item.phase)} (${(item.reason_codes || []).map(readable).join(", ")})`)
            .join("; ")}
        </div>
      )}
      {error && <p role="status" className="mt-4 text-xs text-white/50">{error}</p>}
      {readModelWarnings.length > 0 && (
        <p className="mt-4 text-xs leading-5 text-amber-200/80" data-testid="health-read-model-warnings">
          Some legacy diagnostic records could not be displayed: {readModelWarnings.join(", ")}
        </p>
      )}

      <HealthSubsection title="Sources and coverage" testId="health-evidence-section">
        <div className="overflow-x-auto border-y border-white/10">
          <table className="w-full min-w-[980px] text-left text-xs">
            <thead className="text-white/45">
              <tr>
                {[
                  "Identity",
                  "Field",
                  "Trust",
                  "Source",
                  "Observed value",
                  "Observed",
                  "Retention",
                  "Evidence hash",
                ].map((heading) => (
                  <th key={heading} className="px-3 py-2 font-medium">{heading}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {evidence.map((item) => (
                <tr key={item.evidence_id} className="border-t border-white/10">
                  <Cell mono>{item.identity_key}</Cell>
                  <Cell mono>{item.field_name}</Cell>
                  <Cell>{readable(item.trust_state)}</Cell>
                  <Cell mono>{item.source_id}</Cell>
                  <Cell mono>{item.safe_value ?? "Not retained"}</Cell>
                  <Cell>{formatDate(item.observed_at)}</Cell>
                  <Cell>{item.expires_at ? formatDate(item.expires_at) : `${item.retention_days || 180} days`}</Cell>
                  <Cell mono>{(item.evidence_hash || item.value_digest)?.slice(0, 16) || "Not recorded"}</Cell>
                </tr>
              ))}
              {!loading && evidence.length === 0 && (
                <tr className="border-t border-white/10">
                  <td colSpan="8" className="px-3 py-4 text-white/45">No bounded field evidence.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </HealthSubsection>

      <HealthSubsection title="Drift and quarantine" testId="health-anomaly-section">
        {anomalies.length ? (
          <ul className="divide-y divide-white/10 border-y border-white/10">
            {anomalies.map((item) => (
              <li key={item.fingerprint} className="py-3 text-xs text-white/60">
                <span className="font-medium text-white/80">{readable(item.severity)}</span>
                {" - "}
                {readable(item.anomaly_type)}: {item.deterministic_reason}
                <span className="ml-2 text-white/40">
                  {readable(item.status)}; {readable(item.recovery_state)}
                  {item.linked_identity_keys?.length
                    ? `; ${item.linked_identity_keys.length} linked identities`
                    : ""}
                </span>
              </li>
            ))}
          </ul>
        ) : <p className="text-xs text-white/45">No persisted anomalies.</p>}
      </HealthSubsection>

      <HealthSubsection title="Snapshots and live sets" testId="health-snapshot-section">
        {snapshots.length ? (
          <ul className="divide-y divide-white/10 border-y border-white/10">
            {snapshots.map((item) => (
              <li key={item.scan_snapshot_id} className="py-3 text-xs text-white/60">
                <span className="font-mono-num text-white/80">{item.scan_snapshot_id}</span>
                {" - "}
                {readable(item.state)} - {item.candidate_count ?? "unknown"} candidates
              </li>
            ))}
          </ul>
        ) : <p className="text-xs text-white/45">No immutable snapshot history.</p>}
      </HealthSubsection>

      <HealthSubsection title="Scheduler and leases" testId="health-lease-section">
        {detail?.execution_lease ? (
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Metadata label="Execution type" value={readable(detail.execution_lease.execution_type)} />
            <Metadata label="Owner" value={detail.execution_lease.owner} mono />
            <Metadata label="Run ID" value={detail.execution_lease.run_id} mono />
            <Metadata label="Heartbeat" value={formatDate(detail.execution_lease.last_heartbeat_at)} />
          </dl>
        ) : <p className="text-xs text-white/45">No active execution lease.</p>}
      </HealthSubsection>

      <HealthSubsection title="Recovery" testId="health-recovery-section">
        {recoveries.length ? (
          <ul className="divide-y divide-white/10 border-y border-white/10">
            {recoveries.map((attempt) => (
              <li key={attempt.recovery_attempt_id} className="py-3 text-xs text-white/60">
                <span className="font-mono-num text-white/80">{attempt.action_id}</span>
                {" - "}
                {readable(attempt.tier)} - {readable(attempt.status)}
              </li>
            ))}
          </ul>
        ) : <p className="text-xs text-white/45">No bounded recovery attempt recorded.</p>}
      </HealthSubsection>

      <HealthSubsection title="Incidents" testId="health-incident-section">
        <label className="mb-3 block max-w-56 text-xs text-white/55" htmlFor="health-incident-filter">
          Incident filter
          <select
            id="health-incident-filter"
            value={incidentFilter}
            onChange={(event) => setIncidentFilter(event.target.value)}
            className="mt-2 block w-full border border-white/15 bg-[var(--cc-navy)] px-3 py-2 text-sm text-white"
          >
            <option value="active">Active</option>
            <option value="resolved">Resolved</option>
            <option value="all">All</option>
          </select>
        </label>
        {visibleIncidents.length ? (
          <ul className="divide-y divide-white/10 border-y border-white/10">
            {visibleIncidents.map((incident) => (
              <li key={incident.fingerprint} className="py-3 text-xs text-white/60">
                <div>
                  <span className="font-medium text-white/80">{readable(incident.status)}</span>
                  {" - "}
                  {readable(incident.anomaly_type)} - {incident.deterministic_reason}
                </div>
                {incident.status !== "resolved" && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => updateIncident(incident.fingerprint, "acknowledge")}
                      className="border border-white/20 px-3 py-1.5 text-xs text-white/70"
                    >
                      Acknowledge
                    </button>
                    <button
                      type="button"
                      onClick={() => updateIncident(
                        incident.fingerprint,
                        incident.status === "snoozed" ? "unsnooze" : "snooze",
                      )}
                      className="border border-white/20 px-3 py-1.5 text-xs text-white/70"
                    >
                      {incident.status === "snoozed" ? "Unsnooze" : "Snooze 24h"}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        ) : <p className="text-xs text-white/45">No incidents match this filter.</p>}
      </HealthSubsection>

      <div className="mt-6 overflow-x-auto border-y border-white/10">
        <table className="w-full min-w-[760px] text-left text-xs" data-testid="health-shadow-history">
          <thead className="text-white/45">
            <tr>
              {[
                "Completed",
                "Phase / mode",
                "Evaluated state",
                "Evaluation",
                "Reasons",
              ].map((heading) => <th key={heading} className="px-3 py-2 font-medium">{heading}</th>)}
            </tr>
          </thead>
          <tbody>
            {history.map((run) => (
              <tr key={run.health_run_id} className="border-t border-white/10 align-top">
                <Cell>{formatDate(run.completed_at)}</Cell>
                <Cell>{readable(run.phase)}<br /><span className="text-white/45">{readable(run.mode)}</span></Cell>
                <Cell>{readable(run.shadow_health_state)}</Cell>
                <Cell>{readable(run.evaluation_status)}</Cell>
                <Cell>{(run.quality_reasons || []).map(readable).join(", ") || "No recorded reason"}</Cell>
              </tr>
            ))}
            {!loading && history.length === 0 && (
              <tr className="border-t border-white/10">
                <td colSpan="5" className="px-3 py-4 text-white/45">No health evaluation history.</td>
              </tr>
            )}
            {loading && (
              <tr className="border-t border-white/10">
                <td colSpan="5" className="px-3 py-4 text-white/45">Loading health history...</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function HealthSubsection({ title, testId, children }) {
  return (
    <section className="mt-6 border-t border-white/10 pt-5" data-testid={testId}>
      <h4 className="mb-3 text-sm font-semibold text-white/80">{title}</h4>
      {children}
    </section>
  );
}

function formatDate(value) {
  if (!value) return "No history";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "Unknown" : parsed.toLocaleString("en-GB");
}

function Metadata({ label, value, mono = false, breakAll = false, testId }) {
  return (
    <div data-testid={testId}>
      <dt className="text-[11px] uppercase tracking-wide text-white/40">{label}</dt>
      <dd className={`mt-1 text-sm text-white/80 ${mono ? "font-mono-num" : ""} ${breakAll ? "break-all" : ""}`}>
        {value || "None declared"}
      </dd>
    </div>
  );
}

function ContractTable({ title, headings, children }) {
  const headingId = `health-${title.toLowerCase().replaceAll(" ", "-")}`;
  return (
    <section className="border-t border-white/10 py-6" aria-labelledby={headingId}>
      <h3 id={headingId} className="font-display text-lg font-semibold">{title}</h3>
      <div className="mt-4 overflow-x-auto border-y border-white/10">
        <table className="w-full min-w-[900px] text-left text-xs">
          <thead className="text-white/45">
            <tr>{headings.map((heading) => <th key={heading} className="px-3 py-2 font-medium">{heading}</th>)}</tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </section>
  );
}

function Cell({ children, mono = false }) {
  return (
    <td className={`px-3 py-3 leading-5 text-white/65 ${mono ? "font-mono-num break-words" : ""}`}>
      {children}
    </td>
  );
}
